import styled from '@emotion/styled';
import React, { useRef, forwardRef, useEffect, type ComponentProps } from 'react';
import { useButton, useFocusRing, mergeProps } from 'react-aria';
import type { AriaButtonProps } from 'react-aria';

import { mergeRefs } from '../../lib/mergeRefs';
import { Glyph } from '../common/Glyph';

import { getIconScaleHover } from '../../theme/functions/getIconScale';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';
import glyphStyles from '../common/Glyph.module.css';

interface Props extends AriaButtonProps<'button'> {
  className?: string;
  title?: string;
  color?: string;
  hoverColor?: string;
  url?: string;
  size?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any;
  keyedAutoFocus?: string;
}

interface CompoundedComponent extends React.ForwardRefExoticComponent<
  Props & React.RefAttributes<HTMLButtonElement>
> {
  S: typeof S;
  Icon: typeof Glyph;
}

export const IconButton = forwardRef((props, forwardedRef) => {
  const ref = useRef<HTMLButtonElement>(null);
  const button = useButton(props, ref as React.RefObject<Element>);
  const focusRing = useFocusRing({});

  useEffect(() => {
    if (props.keyedAutoFocus != null) {
      ref.current?.focus();
    }
  }, [props.keyedAutoFocus]);

  return (
    <IconButton.S.Root
      {...mergeProps(focusRing.focusProps, button.buttonProps)}
      ref={mergeRefs([ref, forwardedRef])}
      style={{
        ...props.style,
        '--icon-scale-pressed': button.isPressed ? 1 : undefined,
        '--icon-scale-hover': getIconScaleHover(props.size ?? theme.icon.size_default),
        '--icon-color': props.color ?? theme.color.icon_light,
        '--icon-hover-color': props.hoverColor,
      }}
      className={props.className}
      title={props.title}
    >
      {props.url ? (
        <Glyph
          source={props.url}
          color={'var(--icon-color)'}
          size={props.size as ComponentProps<typeof Glyph>['size']}
        />
      ) : null}
    </IconButton.S.Root>
  );
}) as CompoundedComponent;

IconButton.Icon = Glyph;

function S() {}
IconButton.S = S;

S.Root = styled.button`
  .${glyphStyles.root} {
    transition: ${motion.duration.fast} ${motion.curve.fast_in};
    transition-property: transform, color;
  }

  &:hover .${glyphStyles.root} {
    color: var(--icon-hover-color, var(--icon-color));
    transform: scale(var(--icon-scale-pressed, var(--icon-scale-hover)));
  }
`;
